import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useRole } from '@/hooks/useRole';
import {
  NO_ORG_REDIRECT_PATH,
  dashboardPathForRole,
  type Role,
} from '@/types/roles';
import { PageLoader } from '@/components/ui/loading-spinner';
import { isPasswordRecoveryFlowForUser } from '@/lib/authRecovery';

interface ProtectedRouteProps {
  allowedRoles: Role[];
}

export function ProtectedRoute({ allowedRoles }: ProtectedRouteProps) {
  const { role, isLoading, isAuthenticated, user } = useRole();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isPasswordRecoveryFlowForUser(user?.id)) {
    return <Navigate to="/reset-password" replace />;
  }

  if (!role) {
    // User is authenticated but has no role (no org membership)
    return <Navigate to={NO_ORG_REDIRECT_PATH} replace />;
  }

  if (!allowedRoles.includes(role)) {
    // Redirect users with a valid role to their own dashboard.
    return <Navigate to={dashboardPathForRole(role)} replace />;
  }

  return <Outlet />;
}
